import { IconBrandWhatsapp } from '@tabler/icons-react';
import { Lock } from 'lucide-react';
import type { RefObject } from 'react';

import { salesWhatsappLink } from '@constants/contact';
import { Button } from '@/components/lumen/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/lumen/dialog';
import { useLanguage } from '@contexts/LanguageContext';

export function waLink(moduleLabel?: string): string {
  return salesWhatsappLink(
    `Halo Admin Lumen, saya ingin mengaktifkan modul ${moduleLabel ?? 'yang terkunci'} untuk perusahaan kami.`
  );
}

interface FeatureLockedModalProps {
  opened: boolean;
  onClose: () => void;
  /** Sidebar label of the locked module, e.g. "Kepabeanan". */
  moduleLabel?: string;
  /**
   * Where focus goes when the dialog closes, for callers that open it from a layer that is gone by then
   * (the ⌘K palette). Without it, focus returns to whatever had it when the dialog opened.
   */
  returnFocusRef?: RefObject<HTMLElement | null>;
}

/** "Modul {module} belum aktif …" with the module's name in bold, wherever the language puts it. */
export function ModuleInactive({ template, moduleLabel }: { template: string; moduleLabel: string }) {
  const [before, after = ''] = template.split('{module}');
  return (
    <>
      {before}
      <b className="font-semibold text-fg">{moduleLabel}</b>
      {after}
    </>
  );
}

/**
 * Shown when a user clicks a module their company has disabled (the rail, the ⌘K palette).
 * Copy mirrors BillingStatusBanner's tone: the path forward is contacting
 * the Admin to arrange payment/activation.
 */
export default function FeatureLockedModal({ opened, onClose, moduleLabel, returnFocusRef }: FeatureLockedModalProps) {
  const { t } = useLanguage();

  return (
    <Dialog
      open={opened}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="max-w-md"
        onCloseAutoFocus={(event) => {
          const target = returnFocusRef?.current;
          if (!target?.isConnected) return;
          event.preventDefault();
          target.focus();
        }}
      >
        <DialogHeader className="items-center gap-4 text-center sm:text-center">
          <span
            aria-hidden
            className="flex size-14 items-center justify-center rounded-full bg-surface-2 text-fg-muted ring-1 ring-line"
          >
            <Lock className="size-7" />
          </span>
          <DialogTitle>{t('lumen.featureLock.title')}</DialogTitle>
          <DialogDescription className="text-center">
            {moduleLabel ? (
              <ModuleInactive template={t('lumen.featureLock.moduleInactive')} moduleLabel={moduleLabel} />
            ) : (
              t('lumen.featureLock.thisModuleInactive')
            )}{' '}
            {t('lumen.featureLock.contactAdmin')}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Button asChild className="w-full">
            <a href={waLink(moduleLabel)} target="_blank" rel="noopener noreferrer">
              <IconBrandWhatsapp aria-hidden />
              {t('lumen.featureLock.contactSales')}
            </a>
          </Button>
          <Button variant="secondary" className="w-full" onClick={onClose}>
            {t('lumen.featureLock.understood')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
